import { useState } from 'react';
import clsx from 'clsx';
import { setOrderDatumWindow, OrderConflictError } from '../../lib/orders';

interface DatumWindowDialogProps {
  orderId: string;
  side: 'pickup' | 'delivery';
  initialDatum: string | null;
  onClose: () => void;
  onSaved: (nieuweLastModifiedDate: string | null) => void;
}

const SIDE_LABEL: Record<'pickup' | 'delivery', string> = {
  pickup: 'ophaaldatum',
  delivery: 'afleverdatum',
};

function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function DatumWindowDialog({
  orderId,
  side,
  initialDatum,
  onClose,
  onSaved,
}: DatumWindowDialogProps) {
  const initiallyEnabled = initialDatum != null && initialDatum.length > 0;
  const [enable, setEnable] = useState<boolean>(initiallyEnabled);
  const [datum, setDatum] = useState<string>(initialDatum ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const label = SIDE_LABEL[side];
  const today = todayIso();
  const datumValid = !enable || (datum.length === 10 && datum >= today);
  const canSubmit = !submitting && (enable ? datumValid : true);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const sidePayload = enable
        ? { enable: true, datum }
        : { enable: false };
      const res = await setOrderDatumWindow(orderId, { [side]: sidePayload });
      onSaved(res.nieuweLastModifiedDate);
      onClose();
    } catch (err) {
      if (err instanceof OrderConflictError) {
        setError('Order is ondertussen gewijzigd. Ververs de pagina en probeer opnieuw.');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Datum-window mutatie mislukt');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">
            Vroegste {label} instellen
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            De afspraak-planning toont vanaf nu alleen routes vanaf deze datum.
            Klantvoorkeur — geen reservering.
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={enable}
              onChange={(e) => setEnable(e.target.checked)}
              disabled={submitting}
            />
            <span className="text-sm text-slate-700">
              Vroegste {label} instellen
            </span>
          </label>

          {enable ? (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Datum
              </label>
              <input
                type="date"
                min={today}
                value={datum}
                onChange={(e) => setDatum(e.target.value)}
                disabled={submitting}
                className="px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {datum && datum < today && (
                <p className="text-xs text-amber-600 mt-1">
                  Datum mag niet in het verleden liggen.
                </p>
              )}
            </div>
          ) : initiallyEnabled ? (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              De {label}-restrictie wordt gewist. De drawer toont weer alle
              eerstvolgende routes.
            </div>
          ) : null}

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              !canSubmit
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-slate-900 text-white hover:bg-slate-800'
            )}
          >
            {submitting ? 'Opslaan…' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
